import { useState, useCallback } from 'react';
import { getRouteApi, useNavigate, Link } from '@tanstack/react-router';
import { AuthLoginPage, AuthImagePanel } from '@discovr/ui';
import { Heart } from '@discovr/ui/design-system/icons';
import { useAuth, useDemoPrefill, useLoginErrorSync } from '@discovr/common';
import { usePostLoginRedirect } from '@/auth/usePostLoginRedirect';
import { usePendingClaimReturnPath } from '@/auth/pendingClaimReturn';
import { shopifyCopy } from '@discovr/features/copy';

// Generic, non-enumerating auth error: identical for a wrong password, unknown
// account, and cross-app rejection so none reveals anything about the account.
const GENERIC_AUTH_ERROR = 'Invalid email or password.';

// The only account try.getdiscovr.ai may pre-fill here (see useDemoPrefill).
const DEMO_EMAILS = ['demo@getdiscovr.ai'];

// The claim token lives in sessionStorage (this tab only); the 24 h is the BE's
// connection-link expiry, shared with the recovery section.
const CLAIM_LINK_HOURS = shopifyCopy.unlinked.linkLifetimeMs / 3_600_000;
const CLAIM_SIGN_IN_NOTICE = 'Sign in to finish connecting your Shopify store.';
const CLAIM_WAITLIST_NOTE = `Your Shopify install is waiting in this browser tab. If you close the tab, open Discovr AI from Shopify again. The connection link expires ${CLAIM_LINK_HOURS} hours after you installed.`;

// Figma: Heart icon, duotone weight (opacity-based two-vector treatment)
const HeartDuotone = ({ size, className }: { size?: number; className?: string }) => (
  <Heart size={size} className={className} weight="duotone" />
);

const loginRoute = getRouteApi('/login');

export default function LoginPage() {
  const navigate = useNavigate();
  const { redirect, access, expired } = loginRoute.useSearch();
  const { user, signIn, signInWithGoogle, accessDenied: crossAppDenied, authCheckError } = useAuth();
  const demoPrefill = useDemoPrefill(DEMO_EMAILS);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const handleLoadingChange = useCallback((v: boolean) => {
    setIsLoading(v);
    setGoogleLoading(v);
  }, []);

  // Auto-redirect once authenticated (e.g. a new tab opened with an existing
  // session, or straight after a successful sign-in) — also stops another tab's
  // sign-in from looping back to this form.
  usePostLoginRedirect(redirect);

  // The AuthProvider gate detects a cross-app account after Firebase sign-in and
  // signs it out, surfaced with the same generic message as a wrong password. A
  // failing auth check (network/5xx/timeout) is surfaced with its real reason.
  useLoginErrorSync({
    crossAppDenied,
    authCheckError,
    genericMessage: GENERIC_AUTH_ERROR,
    setError,
    setIsLoading: handleLoadingChange,
  });

  const urlAccessDenied = access === 'denied';
  const sessionExpired = expired === true;

  const hasPendingClaim = usePendingClaimReturnPath() !== null && !user;

  const alert = urlAccessDenied
    ? {
        type: 'error' as const,
        message: 'You don’t have access. Please sign in with an authorized account.',
      }
    : sessionExpired
      ? { type: 'warning' as const, message: 'Your session has expired. Please sign in again.' }
      : hasPendingClaim
        ? { type: 'info' as const, message: CLAIM_SIGN_IN_NOTICE }
        : null;

  const handleSubmit = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await signIn(email, password);
      // Navigation happens in the effect above once the backend authorizes this
      // account for the Brand app and `user` is exposed — never on Firebase
      // sign-in alone (which would let a cross-app account through transiently).
    } catch (err) {
      const code = (err as { code?: string } | null)?.code ?? '';
      // Operational errors are safe to show (they reveal nothing about the account);
      // every credential outcome collapses to one generic message — no enumeration.
      if (code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (code === 'auth/network-request-failed') {
        setError('Network error. Check your connection.');
      } else {
        setError(GENERIC_AUTH_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      // Navigation happens in the effect above once /auth/login authorizes this account.
    } catch (err) {
      const code = (err as { code?: string } | null)?.code ?? '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        // dismissed — no error
      } else if (code === 'auth/popup-blocked') {
        setError('Your browser blocked the Google sign-in popup. Please allow popups and try again.');
      } else if (code === 'auth/network-request-failed') {
        setError('Network error. Check your connection.');
      } else if (code === 'auth/account-exists-with-different-credential') {
        // Every existing user already has a password provider, so with Firebase's
        // "one account per email address" setting this is the most common outcome
        // of a first Google sign-in attempt — it must not read as a bad password.
        setError('This email already has a password set. Sign in with your password, then link Google from Settings → Account.');
      } else {
        setError(GENERIC_AUTH_ERROR);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <AuthLoginPage
      onSubmit={handleSubmit}
      initialEmail={demoPrefill?.email}
      initialPassword={demoPrefill?.password}
      error={error}
      isLoading={isLoading}
      appName="Discovr"
      alert={alert}
      showForgotPassword
      showLegalLinks
      eyebrow="Welcome back"
      eyebrowIcon={HeartDuotone}
      headline="Creator marketing, built like media."
      subheadline="The fastest way to plan, approve, and ship a creator campaign all in one place."
      sidePanel={<AuthImagePanel />}
      onGoogleSignIn={handleGoogleSignIn}
      googleLoading={googleLoading}
      onOtpSignIn={(otpEmail) => {
        void navigate({ to: '/login/code', search: { email: otpEmail.trim() || undefined, redirect } });
      }}
      bottomSlot={
        <div className="flex flex-col gap-1.5">
          <p className="text-center text-body-sm-regular text-foreground">
            New to Discovr?{' '}
            <Link to="/waitlist" className="text-body-sm-medium text-(--brand-500) hover:underline">
              Join the waitlist
            </Link>
          </p>
          {hasPendingClaim && (
            <p className="text-center text-body-sm-regular text-foreground">{CLAIM_WAITLIST_NOTE}</p>
          )}
          <p className="text-center text-body-sm-regular text-foreground">
            Your team already on Discovr?{' '}
            <Link to="/join" className="text-body-sm-medium text-(--brand-500) hover:underline">
              Request to join
            </Link>
          </p>
        </div>
      }
    />
  );
}
